import { Component, OnInit, Output, Input, EventEmitter, OnChanges, SimpleChanges, HostListener } from '@angular/core';
import * as _ from 'lodash';
import * as d3 from 'd3';
import * as d3Tip from 'd3-tip';
(d3 as any).tip = d3Tip;
import * as dbox from '@dboxjs/core';
import * as moment from 'moment';
import { UtilsService } from '../../services/utils/utils.service';

/**
 * Renders a chart based on data and options using dbox charts
 */
@Component({
  selector: 'app-chart-container',
  templateUrl: './chart-container.component.html',
  styleUrls: ['./chart-container.component.scss']
})
export class ChartContainerComponent implements OnInit, OnChanges {
  /** Emitter when legend has changed (checkboxes where clicked) */
  @Output() legendChanged = new EventEmitter<any>();
  /** Emitter when sort option has changed */
  @Output() sortChanged = new EventEmitter<any>();
  /** Emitter when chart has been drawn */
  @Output() chartDrawn = new EventEmitter<any>();
  /** Emitter when selectxAxisGroupBy has changed  */
  @Output() selectXAxisGroupBy = new EventEmitter<any>();
  /** Emitter when color palette has changed */
  @Output() paletteChanged = new EventEmitter<any>();
  /**
   * Selected chart to draw
   * Valid options: ['bars', 'heatmap', 'spineplot', 'scatter', 'timeline', 'map']
   */
  @Input() selectedChart: string;
  /** Data to be displayed in the chart */
  @Input() dataToDraw: object[];
  /** Selected question where data came from */
  @Input() selectedQuestion: object;
  /** Selected x axis */
  @Input() xAxis: { type: string, slug: string, name: string };
  /** Selexted y axis */
  @Input() yAxis: { type: string, slug: string, name: string };
  /** X axis options definitions */
  @Input() xAxisOptions: { type: string, slug: string, name: string }[];
  /** Y axis options definitions */
  @Input() yAxisOptions: { type: string, slug: string, name: string }[];
  /** Selected collapseBy option */
  @Input() collapseBy: { type: string, slug: string, name: string };
  /** If selectedChart is a bar plot, and has groups, it could be stacked */
  @Input() stackBy: any;
  /** If selectedChart is a barplot and has groups, these groups are defined here */
  @Input() groupedBy: any;
  /** Selected filters */
  @Input() selectedFilters: object;
  /** Whole filter options for the question */
  @Input() filterOptions: object;
  /** Grouped by options list */
  @Input() groupByOptions: string[];
  /** Legend options to be displayed (using an active property) */
  @Input() legendOptions: { id?: string, name: string, active: boolean }[];
  /** Legend title to be displayed */
  @Input() legendTitle: string;
  /** Generated title of whole chart */
  @Input() generatedTitle: string;
  /** Sort by options */
  @Input() sortBy: { showSortOptions: boolean, valuesAscending: boolean, names: boolean, valuesDescending: boolean };
  /** Dbox chart object */
  chart: any;
  /** Name of the sort by option selected */
  @Input() orderBy: string;
  /** Items groupedBy has options */
  @Input() groupByItems?: any[];
  /** Object with charts that are enabled for the view to select */
  @Input() chartsEnabled?: {};
  /** Switch value to display chart or table in mobile version */
  @Input() switchViewMob: boolean;
  /** Color palette selected */
  @Input() colors: string[];
  /** Labels should be visible or not */
  @Input() labelsVisible: boolean;
  /** Date locale format texts */
  locale: any;
  /** Internal optoin for xAxis grouped by option */
  xAxisGroupBy: any;
  /** If labels options should be visible */
  showLabelsOptions = false;
  /** If colors options should be visible */
  showColorsOptions = false;
  /** Filters object with readable names definitions */
  namedFilters: { name: string, slug: string, selected: string }[];
  /** Inner chart width (margin) */
  innerWidth: any;
  /** Data to display in table (format is different from dataToDraw) */
  dataToDrawTable: any;
  /** Hide subdivition options */
  hideSubdivition: boolean;
  /** Check if current agent navigator is mobile */
  isMobile: boolean;

  /** Inits component, injects UtilsService */
  constructor(private utils: UtilsService) { }

  /** Inits default properties */
  ngOnInit() {
    this.isMobile = this.utils.isMobile();
    this.hideSubdivition = true;
    this.paletteChanged.emit(this.colors);
    this.locale = d3.timeFormatLocale({
      'dateTime': '%A, %e %B %Y г. %X',
      'date': '%d.%m.%Y',
      'time': '%H:%M:%S',
      'periods': ['AM', 'PM'],
      'days': ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'],
      'shortDays': ['Dom', 'Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab'],
      'months': ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio',
        'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'],
      'shortMonths': ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic']
    });
    this.innerWidth = window.innerWidth; // Screen width catcher initializing
  }

  /**
   * Called when any @Input changes
   * If labels: display or hide them
   * If dataToDraw: Re-draw chart
   * If selectedFilters: Update named definitions
   */
  ngOnChanges(changes: SimpleChanges) {
    if (changes.hasOwnProperty('labelsVisible')) {
      this.showLabels();
    }
    if (changes.hasOwnProperty('dataToDraw') && this.dataToDraw && this.dataToDraw.length > 0) {
      this.drawChart();
      this.drawTable();
    }
    if (this.selectedFilters && this.selectedQuestion) {
      const fields = _.cloneDeep(this.selectedQuestion['fieldOptions']);
      if (this.selectedQuestion['kind'] === 'columna') {
        fields.push({ name: this.selectedQuestion['categoryName'], slug: 'columns' });
        fields.push({ name: this.selectedQuestion['subcategoryName'], slug: 'categories' });
      } else {
        fields.push({ name: this.selectedQuestion['categoryName'], slug: 'categories' });
      }
      this.namedFilters = Object.keys(this.selectedFilters).filter(o => {
        return _.includes(fields.map(f => f.slug), o);
      }).map(function (o) {
        const found = fields.find(f => f.slug === o);
        return {
          name: found.name,
          slug: o,
          selected: this.selectedFilters[o]
        };
      }.bind(this));
    }
  }

  /**
   * Set styles for when graph displays vertically or horizontally
   */
  customGraphStyles() {
    if (this.selectedChart === 'bars' && this.xAxis && this.xAxis.type === 'linear') {
      return 'horizontal';
    } else if (this.selectedChart === 'bars' && this.xAxis && this.xAxis.type !== 'linear') {
      return 'vertical';
    } else if (this.selectedChart === 'scatter') {
      return 'scatter';
    } else if (this.selectedChart === 'timeline') {
      return 'heatmap';
    }
  }

  /**
   * Check if minimun value of selected axis should be zero, or it contains negative values
   * @param axis Selected axis to check
   */
  currentMinZero(axis) {
    const min = Number(d3.min(this.dataToDraw, o => o[axis.slug]));
    if (min < 0) {
      return false;
    } else {
      return (this.selectedChart === 'scatter' && axis.type === 'linear') ||
        (this.selectedChart === 'bars' && axis.type === 'linear');
    }
  }

  /** Emits when xAxisGroupBy has changed */
  onSelectXAxisGroupBy() {
    this.selectXAxisGroupBy.emit(this.xAxisGroupBy);
  }

  /** Toggle hide or show labels */
  showLabels() {
    if (this.labelsVisible === false) {
      d3.selectAll('.dbox-label').style('display', 'none');
      d3.selectAll('.dbox-label-coefficient').style('display', 'none');
    } else {
      d3.selectAll('.dbox-label').style('display', null);
      d3.selectAll('.dbox-label-coefficient').style('display', null);
    }
  }

  /**
   * Draw chart, only SVG DOM operations
   */
  drawChart() {
    const vm = this;
    if (!this.xAxis || !this.xAxis.name) {
      return null;
    } else {
      /**
       * Draw dbox charts according with selected options
       * (axes, groupby, collapseby, project) and selected chart
       */
      const allContainerH = window['$']('app-chart-container').height() - window['$']('app-chart-container .topic-filters').height();
      const width = (this.innerWidth > 600 || this.isMobile) ?
        window['$']('.empty-chart').width() : window['$']('app-chart-container').width();
      const height = (this.innerWidth > 600 || this.isMobile) ?
        (window['$']('.empty-chart').height() > 200 ? window['$']('.empty-chart').height() : allContainerH - 20) :
        (window['$']('app-chart-container').height() > 200 ? window['$']('app-chart-container').height() : allContainerH - 20);
      const margin = { top: 10, right: 170, bottom: 50, left: 80 };
      if (this.selectedChart === 'bars') {
        margin.top = 30;
        if (this.innerWidth <= 800) {
          margin.left = 40;
          margin.right = 15;
        }
      } else if (this.selectedChart === 'heatmap') {
        margin.right = 150;
        if (this.innerWidth <= 800) {
          margin.top = 20;
          margin.right = 100;
        }
      } else if (this.selectedChart === 'spineplot' && this.innerWidth <= 800) {
        margin.left = 15;
        margin.right = 15;
      }
      d3.select('.empty-chart').selectAll('*').remove();
      d3.selectAll('.d3-tip').remove();
      d3.selectAll('.title-tip').remove();

      const xAxisValues: string[] = _.uniq(this.dataToDraw.map(function (d) { return d[this.xAxis.slug]; }.bind(this)) as string[]).sort();
      const maxFirstWord = d3.max(xAxisValues, o => o ? o.length * 8 : 0);
      const bandWidth = ((width - 250) / xAxisValues.length) * 0.8;

      if (bandWidth < maxFirstWord ||
        (this.selectedChart === 'spineplot' &&
          (+d3.min(this.dataToDraw, function (d) {
            return d[this.collapseBy.slug];
          }.bind(this))) < 10)) { // Too many labels, gonna be vertical
        margin.bottom = 150;
      }

      const maxYValue = +d3.max(this.dataToDraw, function (o) {
        return +(o[this.yAxis.slug]);
      }.bind(this));

      const config = {
        regression: this.xAxis.type === 'linear' && this.yAxis.type === 'linear' && this.selectedChart === 'scatter',
        formatPreffix: this.selectedQuestion['unitsName'] === 'pesos' ? '$' : '',
        formatSuffix: this.selectedFilters['unidad'] === 'Porcentaje' ? '%' : '',
        decimals: 1,
        size: {
          width: width,
          height: height,
          margin: margin,
          legendTranslate: this.innerWidth >= 800 ? 100 : 40 // Only for heatmap legend
        },
        xAxis: {
          enabled: this.selectedChart !== 'heatmap' && this.selectedChart !== 'spineplot' ? true : false,
          scale: this.selectedChart === 'timeline' ?
            'time' : this.xAxis.type === 'linear' ? 'linear' : 'band',
          minZero: this.currentMinZero(this.xAxis),
          ticks: {
            enabled: this.selectedChart === 'scatter' || this.selectedChart === 'bars' && this.xAxis.type === 'linear' ? true : false,
            style: 'dashLine',
            ticks: this.selectedChart === 'timeline' &&
              this.selectedQuestion &&
              this.selectedQuestion['minorFrequency'] !== 'anual' ?
              (d3.extent(xAxisValues.map(o => +o))[1] > 100000 ? 5 : 8) : 5,
            format: this.selectedChart === 'timeline' &&
              this.selectedQuestion &&
              this.selectedQuestion['minorFrequency'] !== 'anual' ?
              this.formatDatesForTimeline() : undefined,
          }
        },
        yAxis: {
          enabled: this.selectedChart !== 'heatmap' && this.selectedChart !== 'spineplot' ? true : false,
          scale: this.yAxis.type === 'linear' ? 'linear' : 'band',
          minZero: this.currentMinZero(this.yAxis),
          ticks: {
            enabled: this.selectedChart === 'bars' && this.yAxis.type !== 'linear' ? false : true,
            style: 'dashLine'
          }
        },
        events: {
          onClickLegend: function (d, i) {
            if (vm.legendOptions.filter(o => o.active).length > 1 || !d.active) {
              vm.legendOptions[i].active = !vm.legendOptions[i].active;
              vm.legendChanged.emit(vm.legendOptions);
            }
          },
          load: function (chart) {
            if (chart._config.x === 'Año' && vm.selectedChart === 'bars') {
              chart._svg.selectAll('.x.axis .tick text')
                .attr('transform', 'translate(-14, 18)rotate(-90)');
            }
            d3.selectAll('.x.axis .tick text').each(function (tt) {
              if (d3.select(this).attr('transform') && d3.select(this).attr('transform').search('rotate') !== -1) {
                d3.select(this).style('font-size', '11');
              }
            });
            vm.showLabels();
            if (this.selectedChart === 'scatter') {
              d3.selectAll('.empty-chart svg rect').style('opacity', 0.5);
            }
            vm.chartDrawn.emit(chart);
          }
        },
        legendEnabled: this.innerWidth >= 800 ? true : false
      };

      this.chart = dbox.chart(config)
        .bindTo('.empty-chart')
        .data({
          'raw': this.dataToDraw // Modified data by selectedChart (parse dates or fill NAs)
        });

      // If timeline and no groupby, reset legend options
      if (this.selectedChart === 'timeline' && this.groupByOptions.length === 0) {
        vm.legendOptions = [{ name: 'Total', active: true }];
      }

      if (this.selectedChart !== 'heatmap') {
        this.chart.legend(this.legendOptions);
        this.chart.legendTitle(this.legendTitle);
        this.chart.legendType('checkbox');
      }

      switch (this.selectedChart) {
        case 'bars':
          this.drawBars();
          break;
        case 'heatmap':
          this.drawHeatmap();
          break;
        case 'spineplot':
          this.drawSpineplot();
          break;
        case 'scatter':
          this.drawScatter();
          break;
        case 'timeline':
          this.drawTimeline();
          break;
        case 'map':
          this.drawMap();
          break;
        default:
          this.drawBars();
          break;
      }

      this.chart.draw();

      // Update position for axes only for timeline
      if (this.selectedChart === 'timeline') { // Timeline
        d3.selectAll('.x.axis .tick text').attr('dy', 20);
        d3.selectAll('.y.axis .tick text').attr('dx', -10);

        // Remove monthly ticks if variable is year-defined
        if (this.selectedQuestion && this.selectedQuestion['minorFrequency'] === 'anual') {
          d3.selectAll('.x.axis .tick text').each(function (d) {
            if (Number.isNaN(+this['innerHTML'])) {
              d3.select(this['parentNode']).remove();
              return;
            }
          });
        }
      }
    }
  }

  /** Format dates for a timeline chart, it displays month or only year */
  formatDatesForTimeline() {
    const vm = this;
    return function (date) {
      if (d3.timeYear(date) < date) {
        return vm.locale.format('%b')(date);
      } else {
        return vm.locale.format('%Y')(date);
      }
    };
  }

  /** Show labels options */
  showOptions() {
    this.showLabelsOptions = !this.showLabelsOptions;
    this.showColorsOptions = false;
  }

  /** Draw a barplot chart */
  drawBars() {
    const linearAxis = _.find([this.yAxis, this.xAxis], { 'type': 'linear' });
    const bars = this.chart
      .layer(dbox.bars);

    if (this.groupByOptions[0] && this.groupedBy) {
      if (this.stackBy) {
        bars
          .stackBy(this.groupByOptions);
      } else {
        bars
          .groupBy(this.groupByOptions);
      }
      if (this.yAxis.type === 'linear') {
        bars
          .x(this.xAxis.slug)
          .fill(this.xAxis.slug);
      } else {
        bars
          .y(this.yAxis.slug)
          .fill(this.yAxis.slug);
      }
    } else {
      if (this.yAxis.type === 'linear') {
        bars
          .fill(this.xAxis.slug);
      } else {
        bars
          .fill(this.yAxis.slug);
      }
      bars
        .x(this.xAxis.slug)
        .y(this.yAxis.slug);
    }

    if (typeof (this.colors) !== 'function') {
      bars
        .sortBy()
        .colors(this.colors.slice(0, this.legendOptions.length));
    } else {
      bars
        .colors(this.colors);
    }
  }

  /** Draw a heatmap chart */
  drawHeatmap() {
    const heatmap = this.chart
      .layer(dbox.heatmap);

    heatmap
      .x(this.xAxis.slug)
      .y(this.yAxis.slug)
      .fill(this.collapseBy.slug)
      .colorLegend(this.selectedFilters['unidad'])
      .borderRadius(10)
      .sortBy({ 'x': 'asc', 'y': 'desc' })
      .colors(_.cloneDeep(this.colors).reverse());
  }

  /** Draw a spineplot chart */
  drawSpineplot() {
    const spineplot = this.chart
      .layer(dbox.spineplot);
    if (this.groupByOptions.length > 0) {
      spineplot
        .stackBy(this.groupByOptions)
        .fill('key')
        .value(this.collapseBy.slug);
    } else {
      spineplot
        .fill(this.xAxis.slug)
        .value(this.collapseBy.slug);
    }
    spineplot
      .category(this.xAxis.slug)
      .colors(this.colors.slice(0, this.legendOptions.length));
  }

  /** Draw a scatterplot chart */
  drawScatter() {
    const linearAxis = _.find([this.yAxis, this.xAxis], { 'type': 'linear' });
    const collapseBy = this.collapseBy.slug;

    const scatter = this.chart
      .layer(dbox.scatter);

    scatter
      .x(this.xAxis.slug)
      .y(this.yAxis.slug)
      .figure('square')
      .colors(this.colors.slice(0, this.legendOptions.length));

    if (!linearAxis && ![this.yAxis, this.xAxis].map(o => o.slug).includes(collapseBy)) {
      scatter
        .magnitude(collapseBy)
        .magnitudeRange([10, 40]);
    }
    if (this.groupByOptions[0] && this.groupedBy) {
      scatter
        .fill(this.groupedBy);
    } else if (this.yAxis.type === 'ordinal') {
      scatter
        .fill(this.yAxis.slug);
    } else {
      scatter
        .fill(this.xAxis.slug);
    }
  }

  /** Draw a timeline chart */
  drawTimeline() {
    const vm = this;
    const timeline = this.chart
      .layer(dbox.timeline)
      .curve(d3.curveLinear)
      .parseDate('%Y-%m-%d%Z')
      .tip(function (d) {
        let html = '';
        html += d.name !== 'factor_expansion' ? d.name + '<br>' : '';
        moment.locale('es-MX');
        let format = '%Y';
        if (vm.selectedQuestion['minorFrequency'] &&
          (vm.selectedQuestion['minorFrequency'] !== 'anual') &&
          vm.xAxis.slug !== 'Año') {
          format += '-%b';
        }
        html += vm.locale.format(format)(d.x) + '<br>';

        if (d.y !== undefined) {
          /** Uses dbox version 0.0.9 */
          html += ('<span>' + (Number.isNaN(+d.y) ? d.y : timeline.utils.format()(d.y)) + '</span></br>');
        }
        return html;
      });

    if (this.groupByOptions.length > 0) {
      timeline
        .series(this.groupByOptions);
    } else { // SET LINEAR AXIS
      timeline
        .series([this.yAxis.slug]);
    }
    timeline
      .x(this.xAxis.slug)
      .colors(this.colors.slice(0, this.legendOptions.length));
  }

  /** Draw a map using leaflet inside dbox */
  drawMap() {
    const vm = this;
    /**
     * Set options to build a leaflet map
     */
    const width = window['$']('.empty-chart').width();
    const height = window['$']('.empty-chart').height();

    d3.select('.empty-chart').text('');
    d3.select('.empty-chart').selectAll('*').remove();
    d3.select('.empty-chart').append('div')
      .attr('id', 'leaflet-wrapper')
      .style('width', (width - 200) + 'px')
      .style('height', height + 'px');

    const lconfig = {
      'formatPreffix': this.selectedQuestion['unitsName'] === 'pesos' &&
        this.collapseBy.slug !== 'factor_expansion' ? '$' : '',
      'formatSuffix': this.selectedFilters['unidad'] === 'Porcentaje' ? '%' : '',
      'size': {
        width: width,
        height: height,
        margin: { top: 20, right: 20, bottom: 30, left: 40 },
      },
      'bindTo': 'leaflet-wrapper',
      'xAxis': {
        'enabled': false
      },
      'yAxis': {
        'enabled': false
      },
      'map': {
        'topojson': {
          'url': 'assets/topojsons/CVNL.json',
          'objects': ['NuevoLeon', 'AMM', 'MUNS'],
          'translate': [25.4092844, -101.2450029],
          'zoom': 7,
          'maxZoom': 10,
          'minZoom': 5,
          'parser': function (d) {
            return d.id;
          },
          'id': function (d) {
            return 'municipio-' + d.id;
          }
        },
        'formatLegend': function (number) {
          if (number >= 100000) {
            return d3.format('s')(number);
          } else {
            /** Uses dbox version 0.0.9 */
            return vm.chart.helper.utils.format()(number);
          }
        }
      },
      'events': {
        'load': function (chart) {
          vm.chartDrawn.emit(chart);
        }
      }
    };

    const data = this.dataToDraw;
    this.chart = dbox.chart(lconfig)
      .data({ 'raw': data });

    this.chart.layer(dbox.leaflet)
      .id('id')
      .fill(this.yAxis.slug)
      .opacity(0.9)
      .colors(_.cloneDeep(this.colors).reverse())
      .colorLegend(this.selectedFilters['unidad']);

    const legend = d3.select('.empty-chart')
      .style('display', 'flex')
      .style('flex-direction', 'row')
      .style('flex-wrap', 'nowrap')
      .append('div')
      .style('padding-left', '30px')
      .style('top', 8 + 'px')
      .style('right', 10 + 'px')
      .style('height', height + 'px')
      .style('text-align', 'left');

    const items = legend.selectAll('.legend-items')
      .data(this.legendOptions)
      .enter()
      .append('div');

    items
      .append('input')
      .attr('type', 'checkbox')
      .attr('checked', d => d.active ? true : null)
      .attr('value', d => d.id);
    items
      .append('label')
      .style('color', '#333')
      .style('font-size', '12px')
      .on('click', function (d) {
        // d.active = !d.active;
        if (vm.legendOptions.filter(o => o.active).length > 1 || !d.active) {
          vm.legendOptions[vm.legendOptions.indexOf(d)].active = !vm.legendOptions[vm.legendOptions.indexOf(d)].active;
          d3.select((this['parentNode'] as any))
            .select('input')
            .attr('checked', vm.legendOptions[vm.legendOptions.indexOf(d)].active ? true : null);
          vm.legendChanged.emit(vm.legendOptions);
        }
      })
      .text(d => d.name);
  }

  /** Screen width resizing listener */
  @HostListener('window:resize', ['$event'])
  onResize(event) {
    this.innerWidth = window.innerWidth;
    this.drawChart();
  }

  /** Draw table instead of chart */
  drawTable() {
    this.dataToDrawTable = [];
    let groupOption = {};
    let groupValues = [];
    let value = null;
    if (this.groupedBy) {
      for (let i = 0; i < this.legendOptions.length; i++) {
        // Clear groupValues for each iteration
        groupValues = [];
        // Generate an ordered array for all values that match with current legend option (group).
        // The order corresponds to the appear position of each categorie in dataToDraw.
        for (let j = 0; j < this.dataToDraw.length; j++) {
          if (this.dataToDraw[j][this.legendOptions[i].name] !== undefined) {
            value = this.dataToDraw[j][this.legendOptions[i].name];
            groupValues.push(this.selectedFilters['unidad'] === 'Porcentaje' ? `${value.toFixed(2)}%` : value);
          } else {
            groupValues.push('*');
          }
        }
        groupOption = { 'name': this.legendOptions[i].name, 'values': groupValues };
        this.dataToDrawTable.push(groupOption);
        groupOption = {};
      }
    } else {
      for (let i = 0; i < this.dataToDraw.length; i++) {
        if ((this.dataToDraw[i][this.collapseBy.slug] !== undefined)) {
          value = this.dataToDraw[i][this.collapseBy.slug];
          this.dataToDrawTable.push(this.selectedFilters['unidad'] === 'Porcentaje' ? `${value.toFixed(2)}%` : value);
        } else {
          this.dataToDrawTable.push('*');
        }
      }
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/components/chart-container/chart-container.component.ts